import { useMemo } from "react";
import { Box, Paper, Skeleton, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import { useCentralAlertas } from "../context";
import {
	LABEL_SEVERIDADE,
	SEVERIDADES,
} from "../../../utils/centralDeAlertas/labels";
import { corSeveridade } from "../../../utils/centralDeAlertas/cores";
import useStyles from "./styles";
import { GUIDES_CENTRAL, MetricInfo } from "../metricInfo";

// Mapa de calor dia × hora dos incidentes do período: cor = severidade mais
// grave da hora, intensidade menor quando há um único incidente. Clicar numa
// célula foca o primeiro incidente dela na TabelaIncidentes.

// Sempre os últimos 7 dias do período filtrado.
const MAXIMO_DIAS = 7;
const HORAS = Array.from({ length: 24 }, (_, hora) => hora);
const TITULOS_NO_TOOLTIP = 3;
const SEVERIDADES_LEGENDA = ["critico", "alto", "medio", "baixo"];

export default function MapaCalorIncidentes() {
	const theme = useTheme();
	const styles = useStyles(theme);
	const { agora, carregando, incidentesFiltrados, intervalo, focarIncidente } =
		useCentralAlertas();

	const linhas = useMemo(() => {
		const porHora = new Map();
		incidentesFiltrados.forEach((incidente) => {
			const chave = dayjs(incidente.dataHoraQueda).format("YYYY-MM-DD-H");
			if (!porHora.has(chave)) porHora.set(chave, []);
			porHora.get(chave).push(incidente);
		});

		const ultimoDia = intervalo.fim.startOf("day");
		const totalDias = Math.min(
			MAXIMO_DIAS,
			ultimoDia.diff(intervalo.inicio.startOf("day"), "day") + 1,
		);
		return Array.from({ length: totalDias }, (_, indice) => {
			const dia = ultimoDia.subtract(indice, "day");
			const celulas = HORAS.map((hora) => {
				const lista = porHora.get(`${dia.format("YYYY-MM-DD")}-${hora}`) || [];
				const maisGrave = lista.reduce(
					(atual, incidente) =>
						atual === null ||
						SEVERIDADES.indexOf(incidente.severidade) < SEVERIDADES.indexOf(atual)
							? incidente.severidade
							: atual,
					null,
				);
				return {
					hora,
					lista,
					maisGrave,
					futuro: dia.hour(hora).isAfter(agora),
				};
			});
			return {
				chave: dia.format("YYYY-MM-DD"),
				rotulo: dia.format("DD/MM"),
				semana: dia.isSame(agora, "day") ? "hoje" : dia.format("ddd"),
				hoje: dia.isSame(agora, "day"),
				total: celulas.reduce((soma, celula) => soma + celula.lista.length, 0),
				celulas,
			};
		});
	}, [incidentesFiltrados, intervalo, agora]);

	const tooltipCelula = (linha, celula) => (
		<Box>
			<Typography variant="caption" sx={styles.tooltipTitulo}>
				{`${linha.rotulo} ${String(celula.hora).padStart(2, "0")}h · ${celula.lista.length} incidente(s)`}
			</Typography>
			{celula.lista.slice(0, TITULOS_NO_TOOLTIP).map((incidente) => (
				<Typography key={incidente.id} variant="caption" sx={styles.tooltipLinha}>
					{`${LABEL_SEVERIDADE[incidente.severidade]} · ${incidente.alerta?.titulo || "—"} · ${incidente.equipamento?.nome || "—"}`}
				</Typography>
			))}
			{celula.lista.length > TITULOS_NO_TOOLTIP && (
				<Typography variant="caption" sx={styles.tooltipLinha}>
					{`+${celula.lista.length - TITULOS_NO_TOOLTIP} outros`}
				</Typography>
			)}
		</Box>
	);

	return (
		<Paper sx={styles.container}>
			<Box sx={styles.linhaCabecalho}>
				<Box sx={styles.cabecalho}>
					<Typography variant="mySubtitle">Linha do tempo</Typography>
					<Typography variant="caption" sx={styles.caption}>
						incidentes por hora · passe o mouse para detalhes
					</Typography>
				</Box>
				<Box sx={styles.cabecalho}>
					{SEVERIDADES_LEGENDA.map((severidade) => (
						<Box key={severidade} sx={styles.itemLegenda}>
							<Box sx={styles.quadradoLegenda(corSeveridade(theme, severidade))} />
							<Typography variant="caption" sx={styles.caption}>
								{LABEL_SEVERIDADE[severidade]}
							</Typography>
						</Box>
					))}
					<MetricInfo theme={theme} guide={GUIDES_CENTRAL.mapaCalor} />
				</Box>
			</Box>
			{carregando ? (
				<Skeleton variant="rounded" sx={styles.skeleton} />
			) : (
				<Box sx={styles.grade}>
					<Box sx={styles.linhaGrade}>
						<span />
						{HORAS.map((hora) => (
							<Typography key={hora} variant="caption" sx={styles.rotuloHora}>
								{hora % 3 === 0 ? String(hora).padStart(2, "0") : ""}
							</Typography>
						))}
						<span />
					</Box>
					{linhas.map((linha) => (
						<Box key={linha.chave} sx={styles.linhaGrade}>
							<Typography variant="caption" sx={styles.rotuloDia(linha.hoje)}>
								<b>{linha.rotulo}</b> <Box component="span" sx={styles.caption}>{linha.semana}</Box>
							</Typography>
							{linha.celulas.map((celula) =>
								celula.lista.length ? (
									<Tooltip
										key={celula.hora}
										title={tooltipCelula(linha, celula)}
										arrow
										disableInteractive
									>
										<Box
											role="button"
											aria-label={`${linha.rotulo} ${celula.hora}h: ${celula.lista.length} incidente(s)`}
											onClick={() => focarIncidente(celula.lista[0].id)}
											sx={styles.celula(
												corSeveridade(theme, celula.maisGrave),
												celula.lista.length > 1,
											)}
										/>
									</Tooltip>
								) : (
									<Box key={celula.hora} sx={styles.celulaVazia(celula.futuro)} />
								),
							)}
							<Typography variant="caption" sx={styles.totalDia}>
								{linha.total || ""}
							</Typography>
						</Box>
					))}
				</Box>
			)}
		</Paper>
	);
}
